import { useState } from 'preact/hooks';
import type { TextKey } from '../../content/fr';
import { ASSETS, type AssetDef } from '../../content/market';
import { fmtEur, fmtPct } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { GameState } from '../../engine/state';
import { update } from '../../engine/store';
import { DAYS_PER_YEAR } from '../../engine/time';
import { AV, buyUc, depositToAv, maxUc, sellUc } from '../../game/av';
import {
  assetPer,
  bondYield,
  changeOver,
  debtRatio,
  dividendYield,
  earningsGrowth,
  priceOf,
  volatility,
} from '../../game/market';
import { buyShares, depositToPea, maxShares, sellShares } from '../../game/pea';
import { Card, Line, LineChart } from '../components';
import { fmtNumber, formatDuration, signedEur, signedPct } from '../labels';

/** L'enveloppe dans laquelle on achète et on vend : le PEA, ou l'assurance-vie. */
type Envelope = 'pea' | 'av';

/** L'enveloppe du moment : celle choisie si les deux sont ouvertes, sinon la seule ouverte. */
function envelopeOf(state: GameState, choice: Envelope): Envelope | null {
  if (state.pea.open && state.av.open) return choice;
  if (state.pea.open) return 'pea';
  return state.av.open ? 'av' : null;
}

/** L'onglet Marché : chaque placement avec sa courbe, sa présentation, ses indicateurs, et les achats et ventes. */
export function MarketTab({ state }: { state: GameState }) {
  const [choice, setChoice] = useState<Envelope>('pea');
  const envelope = envelopeOf(state, choice);
  return (
    <div class="grid">
      <CashCard state={state} envelope={envelope} onChoose={setChoice} />
      {ASSETS.map((asset) => (
        <AssetCard key={asset.id} state={state} asset={asset} envelope={envelope} />
      ))}
    </div>
  );
}

/** L'argent disponible pour acheter dans l'enveloppe choisie : on peut en verser d'ici. */
function CashCard(props: { state: GameState; envelope: Envelope | null; onChoose: (envelope: Envelope) => void }) {
  const { state, envelope } = props;
  if (envelope === null) {
    return (
      <Card title={t('market.title')}>
        <p class="muted">{t('market.needPea')}</p>
      </Card>
    );
  }
  const deposit = envelope === 'pea' ? depositToPea : depositToAv;
  const entryFee = AV.contracts[state.av.contract].entryFee;
  return (
    <Card title={t('market.title')}>
      {state.pea.open && state.av.open && (
        <div class="button-row" role="group" aria-label={t('market.envelope')}>
          {(['pea', 'av'] as Envelope[]).map((id) => (
            <button
              key={id}
              class={`button-small ${envelope === id ? 'active' : ''}`}
              aria-pressed={envelope === id}
              onClick={() => props.onChoose(id)}
            >
              {t(id === 'pea' ? 'market.envPea' : 'market.envAv')}
            </button>
          ))}
        </div>
      )}
      {envelope === 'pea' ? (
        <Line label={t('market.cash')} value={fmtEur(state.pea.cash)} tone="strong" />
      ) : (
        <Line label={t('market.avCash')} value={fmtEur(state.av.euros)} tone="strong" />
      )}
      <div class="button-row">
        <button disabled={state.cash < 100} onClick={() => update((s) => deposit(s, 100))}>
          {t('invest.deposit', { amount: fmtEur(100) })}
        </button>
        <button disabled={state.cash < 1000} onClick={() => update((s) => deposit(s, 1000))}>
          {t('invest.deposit', { amount: fmtEur(1000) })}
        </button>
      </div>
      {envelope === 'pea' ? (
        <p class="hint">{t('invest.orderFee')}</p>
      ) : (
        <>
          {entryFee > 0 && (
            <p class="hint">{t('av.entryFee', { rate: fmtPct(entryFee), fee: fmtEur(1000 * entryFee) })}</p>
          )}
          <p class="hint">{t('market.avHint')}</p>
        </>
      )}
    </Card>
  );
}

interface Indicator {
  label: TextKey;
  def: TextKey;
  value: string;
}

/** Les quatre indicateurs d'un placement : ceux d'une action, d'un fonds d'actions ou d'un fonds d'obligations. */
function indicators(state: GameState, asset: AssetDef): Indicator[] {
  const vol = volatility(state, asset.id);
  const volValue = vol === null ? '—' : fmtPct(vol);
  if (asset.kind === 'action') {
    const per = assetPer(state, asset.id);
    const growth = earningsGrowth(state, asset.id);
    const dividend = dividendYield(state, asset.id);
    return [
      { label: 'market.ind.per', def: 'market.def.per', value: per === null ? t('market.losses') : fmtNumber(per) },
      {
        label: 'market.ind.yield',
        def: 'market.def.yield',
        value: dividend > 0 ? fmtPct(dividend) : t('market.noDividend'),
      },
      { label: 'market.ind.growth', def: 'market.def.growth', value: growth === null ? '—' : signedPct(growth) },
      { label: 'market.ind.debt', def: 'market.def.debt', value: fmtPct(debtRatio(state, asset.id)) },
    ];
  }
  const fees = { label: 'market.ind.fees' as TextKey, def: 'market.def.fees' as TextKey, value: fmtPct(asset.fees) };
  const lines = fmtNumber(asset.lines ?? 0);
  if (asset.per === undefined) {
    return [
      fees,
      { label: 'market.ind.bonds', def: 'market.def.lines', value: lines },
      { label: 'market.ind.ytm', def: 'market.def.ytm', value: fmtPct(bondYield(asset.id)) },
      { label: 'market.ind.vol', def: 'market.def.vol', value: volValue },
    ];
  }
  const per = assetPer(state, asset.id);
  return [
    fees,
    { label: 'market.ind.companies', def: 'market.def.lines', value: lines },
    { label: 'market.ind.perAvg', def: 'market.def.perAvg', value: per === null ? '—' : fmtNumber(per) },
    { label: 'market.ind.vol', def: 'market.def.vol', value: volValue },
  ];
}

function Indicators({ state, asset }: { state: GameState; asset: AssetDef }) {
  const items = indicators(state, asset);
  return (
    <>
      <dl class="indicators">
        {items.map((item) => (
          <div key={item.label} title={t(item.def)}>
            <dt>{t(item.label)}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>
      <details class="indicators-help">
        <summary>{t('market.understand')}</summary>
        <ul>
          {items.map((item) => (
            <li key={item.label}>
              <strong>{t(item.label)}</strong> : {t(item.def)}
            </li>
          ))}
        </ul>
      </details>
    </>
  );
}

function AssetCard({ state, asset, envelope }: { state: GameState; asset: AssetDef; envelope: Envelope | null }) {
  const price = priceOf(state, asset.id);
  const inAv = envelope === 'av';
  // Dans l'assurance-vie, seuls les fonds s'achètent : les actions restent au PEA.
  const tradable = envelope === 'pea' || (inAv && AV.ucAssets.includes(asset.id));
  const holding = inAv ? state.av.holdings[asset.id] : state.pea.holdings[asset.id];
  const maxOf = (s: GameState) => (inAv ? maxUc(s, asset.id) : maxShares(s, asset.id));
  const buy = (s: GameState, count: number) => (inAv ? buyUc(s, asset.id, count) : buyShares(s, asset.id, count));
  const sell = (s: GameState, count: number) => (inAv ? sellUc(s, asset.id, count) : sellShares(s, asset.id, count));
  const max = tradable ? maxOf(state) : 0;
  const monthChange = changeOver(state, asset.id, 1);
  const yearChange = changeOver(state, asset.id, 12);
  // Un cours par mois sur dix ans au plus, puis le cours du jour.
  const values = [...state.market.history[asset.id].slice(-120), price];
  const duration = formatDuration(((values.length - 1) * DAYS_PER_YEAR) / 12);
  return (
    <Card title={t(asset.name)}>
      <p class="eyebrow">{t(asset.kind === 'fonds' ? 'invest.kind.fonds' : 'invest.kind.action')}</p>
      <Line label={t('invest.priceLabel')} value={t('invest.price', { price: fmtEur(price) })} tone="strong" />
      <p class={yearChange < 0 ? 'tone-bad' : 'tone-good'}>
        {t('invest.change', { month: signedPct(monthChange), year: signedPct(yearChange) })}
      </p>
      {values.length > 1 && (
        <>
          <LineChart values={values} label={t('market.chartLabel', { name: t(asset.name), duration })} />
          <p class="hint">{t('market.chartSince', { duration })}</p>
        </>
      )}
      <p class="muted">{t(asset.about)}</p>
      <Indicators state={state} asset={asset} />
      {asset.dividend && <p class="hint">{t('invest.dividend', { rate: fmtPct(asset.dividend) })}</p>}
      {holding && (
        <p>
          {t(holding.shares === 1 ? 'invest.holdingOne' : 'invest.holding', {
            shares: holding.shares,
            value: fmtEur(holding.shares * price),
            gain: signedEur(holding.shares * price - holding.cost),
          })}
        </p>
      )}
      {inAv && !tradable && <p class="hint">{t('market.peaOnly')}</p>}
      {tradable && (
        <div class="button-row">
          <button disabled={max < 1} onClick={() => update((s) => buy(s, 1))}>
            {t('invest.buy', { count: 1 })}
          </button>
          <button disabled={max < 10} onClick={() => update((s) => buy(s, 10))}>
            {t('invest.buy', { count: 10 })}
          </button>
          <button disabled={max < 1} onClick={() => update((s) => buy(s, maxOf(s)))}>
            {t('invest.buyMax', { count: max })}
          </button>
          {holding && (
            <>
              <button onClick={() => update((s) => sell(s, 1))}>{t('invest.sell', { count: 1 })}</button>
              <button disabled={holding.shares < 10} onClick={() => update((s) => sell(s, 10))}>
                {t('invest.sell', { count: 10 })}
              </button>
              <button onClick={() => update((s) => sell(s, holding.shares))}>{t('invest.sellAll')}</button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
